import { useState, useEffect, useCallback, useRef } from 'react';
import { Lightbulb, X, Sparkles, Target, Feather } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const FREEZE_TIMEOUT = 45000; // 45 seconds

// Smart nudge generator that uses context
const generateSmartNudge = (content, genre, dynamicGuidance) => {
  // If we have dynamic guidance, use it
  if (dynamicGuidance?.guidance) {
    const g = dynamicGuidance.guidance;
    const smartNudges = [];
    
    // Use focus_prompt (new structure)
    if (g.focus_prompt) {
      smartNudges.push({
        type: 'focus',
        icon: 'sparkles',
        text: g.focus_prompt
      });
    }
    
    // Use story_questions (new structure)
    if (g.story_questions && g.story_questions.length > 0) {
      const randomQuestion = g.story_questions[Math.floor(Math.random() * g.story_questions.length)];
      smartNudges.push({
        type: 'question',
        icon: 'target',
        text: randomQuestion
      });
    }
    
    // Use quick_ideas (new structure)
    if (g.quick_ideas && g.quick_ideas.length > 0) {
      const randomIdea = g.quick_ideas[Math.floor(Math.random() * g.quick_ideas.length)];
      smartNudges.push({
        type: 'idea',
        icon: 'lightbulb',
        text: randomIdea
      });
    }
    
    if (smartNudges.length > 0) {
      return smartNudges[Math.floor(Math.random() * smartNudges.length)];
    }
  }
  
  // Fallback to contextual nudges
  const contextualNudges = generateContextualNudge(content, genre);
  return {
    type: 'generic',
    icon: 'lightbulb',
    text: contextualNudges
  };
};

// Contextual nudges based on content analysis
const generateContextualNudge = (content, genre) => {
  const genericNudges = [
    "Still thinking? Take a breath. What happens next?",
    "Describe what you're seeing in the scene...",
    "Don't worry about perfect words. Just get the idea down.",
    "Close your eyes. What do you hear in this moment?",
    "What's the one thing you want readers to feel here?",
  ];

  const genreNudges = {
    fiction: [
      "What does your character want most right now?",
      "What's stopping them from getting it?",
      "What's the tension in this moment?",
      "What would surprise your reader here?",
    ],
    memoir: [
      "What were you feeling in that exact moment?",
      "What details do you remember most vividly?",
      "Why does this memory matter to you?",
      "What did you learn from this experience?",
      "What would you tell your younger self?",
    ],
    lifestory: [
      "What were you feeling in that exact moment?",
      "What details do you remember most vividly?",
      "Who else was there? What did they do?",
      "What would you tell your younger self?",
    ],
    business: [
      "What's the key takeaway for your reader here?",
      "Can you give a specific example?",
      "What problem does this solve?",
      "How can your reader apply this today?",
    ],
    childrens: [
      "What would make a child laugh here?",
      "Can you make this simpler?",
      "What's the most exciting part?",
      "How does your character feel right now?",
    ],
    shortstory: [
      "What's surprising in this moment?",
      "Can you add more sensory detail?",
      "What is left unsaid?",
      "How does this connect to your theme?",
    ]
  };

  const nudges = genreNudges[genre] || genericNudges;
  return nudges[Math.floor(Math.random() * nudges.length)];
};

function FreezeDetector({ 
  content = '', 
  genre = 'memoir', 
  isRecording = false, 
  chapterId = null,
  dynamicGuidance = null,
  propGuidance = null 
}) {
  const [showNudge, setShowNudge] = useState(false);
  const [currentNudge, setCurrentNudge] = useState(null);
  const [isQuiet, setIsQuiet] = useState(false);
  const timerRef = useRef(null);
  const lastContentRef = useRef(content);

  // Fetch dynamic guidance if available
  const fetchGuidance = useCallback(async () => {
    if (!chapterId) return null;
    try {
      const response = await axios.post(
        `${API}/api/chapters/${chapterId}/writing-guide`,
        { current_text: content },
        
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch guidance:', error);
      return null;
    }
  }, [chapterId, content]);

  const triggerNudge = useCallback(async () => {
    if (isRecording || isQuiet) return;
    
    // Try to get fresh guidance if we don't have it
    let guidance = dynamicGuidance || propGuidance;
    if (!guidance && chapterId && content.length > 100) {
      guidance = await fetchGuidance();
    }
    
    const nudge = generateSmartNudge(content, genre, guidance);
    setCurrentNudge(nudge);
    setShowNudge(true);
  }, [content, genre, dynamicGuidance, propGuidance, isQuiet, chapterId, fetchGuidance, isRecording]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setShowNudge(false);
    
    timerRef.current = setTimeout(() => {
      triggerNudge();
    }, FREEZE_TIMEOUT);
  }, [triggerNudge]);

  // Detect content changes (typing)
  useEffect(() => {
    if (content !== lastContentRef.current) {
      lastContentRef.current = content;
      resetTimer();
    }
  }, [content, resetTimer]);

  // Detect recording state changes
  useEffect(() => {
    if (isRecording) {
      resetTimer();
    }
  }, [isRecording, resetTimer]);

  // Initial timer setup
  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resetTimer]);

  const handleDismiss = () => {
    setShowNudge(false);
    resetTimer();
  };

  const handleQuiet = () => {
    setShowNudge(false);
    setIsQuiet(true);
  };

  const handleReactivate = () => {
    setIsQuiet(false);
    resetTimer();
  };

  // Get icon based on nudge type
  const getNudgeIcon = () => {
    if (!currentNudge) return <Lightbulb className="w-4 h-4 text-purple-600 dark:text-purple-400" />;
    
    switch (currentNudge.icon) {
      case 'sparkles':
        return <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />;
      case 'target':
        return <Target className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'feather':
        return <Feather className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
      default:
        return <Lightbulb className="w-4 h-4 text-purple-600 dark:text-purple-400" />;
    }
  };

  // Get title based on nudge type
  const getNudgeTitle = () => {
    if (!currentNudge) return "Still thinking?";
    
    switch (currentNudge.type) {
      case 'focus':
        return "Keep writing...";
      case 'question':
        return "Story question:";
      case 'idea':
        return "Quick idea:";
      default:
        return "Still thinking?";
    }
  };

  // Get background gradient based on nudge type - Purple/Green theme
  const getNudgeGradient = () => {
    if (!currentNudge) return "from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20";
    
    switch (currentNudge.type) {
      case 'focus':
        return "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20";
      case 'question':
        return "from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20";
      case 'idea':
        return "from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20";
      default:
        return "from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20";
    }
  };

  // Show minimized button when in quiet mode - LEFT SIDE, smaller
  if (isQuiet) {
    return (
      <button
        onClick={handleReactivate}
        className="fixed bottom-4 left-4 sm:left-72 z-40 w-9 h-9 bg-purple-100 dark:bg-purple-900/40 hover:bg-purple-200 dark:hover:bg-purple-800/50 border border-purple-300 dark:border-purple-700 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all hover:scale-105"
        title="Reactivate writing tips"
        data-testid="freeze-nudge-minimized"
      >
        <Lightbulb className="w-4 h-4 text-purple-600 dark:text-purple-400" />
      </button>
    );
  }

  if (!showNudge) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 sm:left-72 right-4 sm:right-auto sm:max-w-md animate-in slide-in-from-bottom-3 z-40"
      data-testid="freeze-nudge"
    >
      <div className={`bg-gradient-to-br ${getNudgeGradient()} border border-purple-200 dark:border-purple-700/50 rounded-xl p-3 shadow-lg backdrop-blur-sm`}>
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-white dark:bg-[#22201C] rounded-lg flex items-center justify-center shadow-sm border border-purple-200 dark:border-purple-700">
              {getNudgeIcon()}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-purple-900 dark:text-purple-100 mb-1">
              {getNudgeTitle()}
            </p>
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
              {currentNudge?.text || "Take a breath. What happens next?"}
            </p>
            <div className="flex items-center space-x-2 mt-2">
              <button
                onClick={handleDismiss}
                className="text-xs px-3 py-1.5 bg-white dark:bg-[#22201C] hover:bg-gray-50 dark:hover:bg-[#2A2520]/50 text-purple-700 dark:text-purple-300 rounded-lg transition-colors font-medium shadow-sm border border-purple-200 dark:border-purple-700"
                data-testid="nudge-dismiss"
              >
                Got it
              </button>
              <button
                onClick={handleQuiet}
                className="text-xs px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-[#F8F5F0]/80 dark:hover:bg-gray-800/50 rounded-lg transition-colors"
                title="Minimize tips"
              >
                Hide
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-0.5 rounded-lg hover:bg-[#F8F5F0]/80 dark:hover:bg-gray-800/50 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default FreezeDetector;
